import React, { useContext, useEffect } from "react";
import { Box } from "@mui/material";
import { ReactComponent as VisualizeFileIcon } from "../../../../assets/icons/visualize-file.svg";
import { ReactComponent as DownloadIcon } from "../../../../assets/icons/download-icon.svg";
import SearchableTable from "../../../../components/Globo/Table/SearchableTable";
import TableLoading from "../../../../components/TableLoading/TableLoading";
import AlertBox from "../../../../components/AlertBox/AlertBox";
import AttachmentsContext from "../../../../context/attachmentsContext";
import useDownloadAttachment from "../../../../hooks/useDownloadAttachment";
import { useCultureHubAssessments } from "../../../../hooks/useCultureHubAssessments";
import { VIEWABLE_FILE_TYPES } from "../../../../shared/constants/store";
import { getFileExtensionFromFileName } from "../../../../shared/utils/getFileExtensionFromFileName";
import { toastError } from "../../../../utils";
import { FONT_INTER } from "../../../../styles/fonts";

const columns = [
  { key: "fileName", label: "Arquivo", sortable: true },
  { key: "relation", label: "Relação", type: "tags", width: 360 },
  { key: "receivedAt", label: "Recebido em", sortable: true, align: "right" },
  { key: "actions", label: "Ações", type: "actions", align: "right" },
];

const CultureHubAssessments = ({ contentId }) => {
  const { items, loading, error } = useCultureHubAssessments({ contentId });
  const { setFileToView } = useContext(AttachmentsContext);
  const { downloadAttachment } = useDownloadAttachment();

  useEffect(() => {
    if (error) toastError("Erro ao carregar pareceres do Hub Cultura.");
  }, [error]);

  const rows = items.map((item) => {
    const fileToDownload = {
      fileData: { id: item.reportAttachmentId, fileName: item.fileName },
      contentId,
      origin: "pareceres-hub-cultura",
      mode: 0,
    };
    const fileToVisualize = {
      ...fileToDownload,
      origin: "visualizador-pareceres-hub-cultura",
      mode: 1,
      autoSave: false,
    };
    const canViewFile = VIEWABLE_FILE_TYPES.includes(
      getFileExtensionFromFileName(item.fileName)
    );

    return {
      ...item,
      actions: [
        {
          label: "Visualizar",
          icon: <VisualizeFileIcon width={24} height={24} />,
          disabled: !canViewFile,
          action: () => setFileToView(fileToVisualize),
        },
        {
          label: "Baixar",
          icon: <DownloadIcon width={24} height={24} />,
          action: () => downloadAttachment(fileToDownload),
        },
      ],
    };
  });

  if (loading) {
    return <TableLoading />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 1160, mx: "auto", fontFamily: FONT_INTER }}>
        <AlertBox
          hasCloseIcon={false}
          text="Erro ao carregar pareceres do Hub Cultura. Por favor, contacte o administrador."
          type="error"
          color="#ff9494"
        />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box sx={{ p: 3, maxWidth: 1160, mx: "auto", fontFamily: FONT_INTER }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 240,
            backgroundColor: "#fff",
            borderRadius: 2,
            color: "#939598",
            fontSize: "20px",
          }}
        >
          Nenhum parecer disponível.
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        maxWidth: 1160,
        mx: "auto",
        mt: "12px",
        fontFamily: FONT_INTER,
      }}
    >
      <SearchableTable
        columns={columns}
        data={rows}
        rowsPerPage={10}
        itemsLabel="arquivos"
        loading={loading}
        searchPlaceholder="Buscar por nome, parecer, data..."
        emptyStateMessage="Nenhum parecer encontrado."
      />
    </Box>
  );
};

export default CultureHubAssessments;
